// ============================================
// Seed Templates: HIPAA Compliance Documents
// Category: hipaa
// Templates: 13
// ============================================

import type { DocTemplate, QualityGate } from '../../types/docops';

const BAA_QUALITY_GATE: QualityGate = {
  requiredScore: 95,
  maxAttempts: 3,
  provisions: [
    { id: 'baa-permitted-uses', title: 'Permitted Uses/Disclosures', required: true, keywords: ['permitted use', 'permitted disclosure', 'use and disclosure', 'disclose'], weight: 'critical' },
    { id: 'baa-safeguards', title: 'Appropriate Safeguards', required: true, keywords: ['safeguard', 'administrative', 'technical', 'physical'], weight: 'critical' },
    { id: 'baa-breach-notify', title: 'Breach Notification', required: true, keywords: ['breach', 'notification', 'without unreasonable delay', '60 day'], weight: 'critical' },
    { id: 'baa-phi-termination', title: 'PHI Return/Destroy on Termination', required: true, keywords: ['return', 'destroy', 'upon termination', 'phi'], weight: 'critical' },
    { id: 'baa-subcontractor', title: 'Subcontractor Restrictions', required: true, keywords: ['subcontractor', 'downstream', 'agent', 'same restrictions'], weight: 'critical' },
    { id: 'baa-hipaa-ref', title: 'HIPAA/CFR Reference', required: true, keywords: ['hipaa', '45 cfr', '164.504', 'health insurance portability'], weight: 'critical' },
    { id: 'baa-individual-access', title: 'Individual Access to PHI', required: true, keywords: ['individual access', 'access to phi', 'access rights'], weight: 'high' },
    { id: 'baa-amendment', title: 'PHI Amendment Availability', required: true, keywords: ['amendment', 'amend', 'correct'], weight: 'high' },
    { id: 'baa-accounting', title: 'Accounting of Disclosures', required: true, keywords: ['accounting', 'disclosure', 'accounting of disclosures'], weight: 'high' },
    { id: 'baa-hhs', title: 'HHS Availability', required: true, keywords: ['hhs', 'secretary', 'department of health', 'available to'], weight: 'high' },
    { id: 'baa-term', title: 'Term and Termination', required: true, keywords: ['term', 'termination', 'effective date'], weight: 'high' },
  ],
};

export const SEED_TEMPLATES_HIPAA: DocTemplate[] = [
  // ── 5.1 BAA: LLC ↔ PC ──
  {
    id: '5.1-baa-llc-pc',
    categoryId: 'hipaa',
    qualityGate: BAA_QUALITY_GATE,
    title: 'BAA: LLC ↔ PC',
    description:
      'Business Associate Agreement between the MSO LLC (Business Associate) and Professional Corporation (Covered Entity). Required before any PHI flows through the platform. LAUNCH BLOCKER.',
    templateContent: `# Business Associate Agreement

**Between:**
- {{pcEntityName}} ("Covered Entity")
- {{brandEntity}} ("Business Associate")

Effective Date: {{effectiveDate}}

## Permitted Uses and Disclosures
{{AI_GENERATED_CONTENT}}

## Business Associate Obligations
{{AI_GENERATED_CONTENT}}

## Safeguards
{{AI_GENERATED_CONTENT}}

## Breach Notification
{{AI_GENERATED_CONTENT}}

## Term and Termination
{{AI_GENERATED_CONTENT}}

## Effect of Termination on PHI
{{AI_GENERATED_CONTENT}}

Contact: {{legalEmail}}
`,
    requiredVariables: [
      'brandEntity',
      'pcEntityName',
      'legalEmail',
      'effectiveDate',
    ],
    generationPrompt: `DOCUMENT TYPE: HIPAA Business Associate Agreement (BAA) per 45 CFR §164.504(e).
THIS IS A HIPAA COMPLIANCE DOCUMENT — NOT a management services agreement.

PARTIES:
- Covered Entity: {{pcEntityName}}
- Business Associate: {{brandEntity}}
- Effective Date: {{effectiveDate}}
- Contact: {{legalEmail}}

MANDATORY SECTIONS — every section below MUST appear with the exact keywords shown in [brackets]:

## 1. Permitted Uses and Disclosures
Define permitted use and permitted disclosure of PHI. Include specific permitted uses: health tech platform management, AI-assisted lab data analysis, clinical protocol generation. Use the phrase "use and disclosure" and the word "disclose".

## 2. Appropriate Safeguards
Business Associate shall implement appropriate safeguard measures including administrative, technical, and physical safeguards to protect PHI per 45 CFR §164.308, §164.310, and §164.312. ALL FOUR WORDS MUST APPEAR: "safeguard", "administrative", "technical", "physical".

## 3. Breach Notification
Business Associate shall report any breach to Covered Entity without unreasonable delay and no later than 60 days after discovery. Include notification procedures and content requirements. MUST USE: "breach", "notification", "without unreasonable delay", "60 day".

## 4. PHI Return/Destruction on Termination
Upon termination, Business Associate shall return or destroy all PHI. If return or destruction is infeasible, protections extend. MUST USE: "return", "destroy", "upon termination", "PHI".

## 5. Subcontractor Restrictions
Business Associate shall ensure any subcontractor or downstream agent that creates, receives, maintains, or transmits PHI agrees to the same restrictions and conditions. MUST USE: "subcontractor" and either "downstream" or "agent" and "same restrictions".

## 6. HIPAA Regulatory References
This Agreement is required under HIPAA and specifically 45 CFR §164.504(e). Reference the Health Insurance Portability and Accountability Act throughout. MUST USE: "HIPAA", "45 CFR", "164.504".

## 7. Individual Rights
- Individual access to PHI: covered individuals have access rights to their PHI
- PHI amendment: Business Associate shall make PHI available for amendment
- Accounting of disclosures: Business Associate shall maintain an accounting of disclosures
- HHS availability: Business Associate shall make practices and records available to the Secretary of HHS
MUST USE: "individual access", "amendment", "accounting", "HHS" or "Secretary".

## 8. Term and Termination
Specify term length, termination triggers (material breach), and effect of termination on obligations.

FORMAT: Complete, attorney-ready legal document with numbered sections, formal legal language, signature blocks. This is a standardized HIPAA document — generate it in full, not as an outline.`,
    attorneyReviewRequired: true,
    launchBlocker: true,
    estimatedPages: 8,
    sortOrder: 1,
    generationRound: 3,
    dependencies: ['1.1-terms-of-service', '1.2-privacy-policy'],
    anchorSections: ['§3', '§4', '§7', '§12'],
    inheritedProvisions: ['cpom-firewall'],
  },

  // ── 5.2 BAA: PC ↔ Telehealth Network ──
  {
    id: '5.2-baa-pc-telehealth',
    categoryId: 'hipaa',
    title: 'BAA: PC ↔ Telehealth Network',
    description:
      'Business Associate Agreement between the Professional Corporation (Covered Entity) and Telehealth Network (Business Associate) for telehealth consultation services.',
    templateContent: `# Business Associate Agreement

**Between:**
- {{pcEntityName}} ("Covered Entity")
- {{telehealthNetwork}} ("Business Associate")

Effective Date: {{effectiveDate}}

## Permitted Uses and Disclosures
{{AI_GENERATED_CONTENT}}

## Business Associate Obligations
{{AI_GENERATED_CONTENT}}

## Safeguards
{{AI_GENERATED_CONTENT}}

## Breach Notification
{{AI_GENERATED_CONTENT}}

## Term and Termination
{{AI_GENERATED_CONTENT}}

Contact: {{legalEmail}}
`,
    requiredVariables: [
      'pcEntityName',
      'telehealthNetwork',
      'legalEmail',
      'effectiveDate',
    ],
    generationPrompt:
      'Generate HIPAA BAA between {{pcEntityName}} (Covered Entity) and {{telehealthNetwork}} (Business Associate) for telehealth consultation services. Permitted uses: scheduling, conducting, and documenting telehealth consultations, prescription issuance, follow-up communications. Note: {{telehealthNetwork}} may provide their own BAA template — this serves as reference/backup. Format: complete legal document.',
    attorneyReviewRequired: true,
    launchBlocker: false,
    estimatedPages: 8,
    sortOrder: 2,
    generationRound: 3,
    dependencies: ['5.1-baa-llc-pc'],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 5.3 BAA: LLC ↔ Pharmacy Partner ──
  {
    id: '5.3-baa-llc-pharmacy',
    categoryId: 'hipaa',
    title: 'BAA: LLC ↔ Pharmacy Partner',
    description:
      'Business Associate Agreement between the MSO LLC and pharmacy partner for prescription fulfillment and supplement compounding services.',
    templateContent: `# Business Associate Agreement

**Between:**
- {{brandEntity}} ("Business Associate")
- Pharmacy Partner ("Business Associate / Covered Entity")

Effective Date: {{effectiveDate}}

## Permitted Uses and Disclosures
{{AI_GENERATED_CONTENT}}

## Business Associate Obligations
{{AI_GENERATED_CONTENT}}

## Safeguards
{{AI_GENERATED_CONTENT}}

## Breach Notification
{{AI_GENERATED_CONTENT}}

## Term and Termination
{{AI_GENERATED_CONTENT}}

Contact: {{legalEmail}}
`,
    requiredVariables: [
      'brandEntity',
      'pharmacyPartner',
      'legalEmail',
      'effectiveDate',
    ],
    generationPrompt:
      'Generate HIPAA BAA between {{brandEntity}} (Business Associate) and pharmacy partner (Business Associate/Covered Entity) for prescription fulfillment and supplement compounding services. Permitted uses: receiving prescription orders, dispensing medications, fulfillment shipping, adverse event reporting. Format: complete legal document.',
    attorneyReviewRequired: true,
    launchBlocker: false,
    estimatedPages: 8,
    sortOrder: 3,
    generationRound: 3,
    dependencies: ['5.1-baa-llc-pc'],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 5.4 BAA: LLC ↔ Cloud Provider ──
  {
    id: '5.4-baa-llc-cloud',
    categoryId: 'hipaa',
    title: 'BAA: LLC ↔ Cloud Provider',
    description:
      'Business Associate Agreement between the MSO LLC and cloud infrastructure provider hosting PHI. Reference document noting Google standard BAA covers Firebase services.',
    templateContent: `# Business Associate Agreement

**Between:**
- {{brandEntity}}
- {{cloudProvider}}

Effective Date: {{effectiveDate}}

## Scope of Services
{{AI_GENERATED_CONTENT}}

## Security Measures
{{AI_GENERATED_CONTENT}}

## Google Standard BAA Reference
{{AI_GENERATED_CONTENT}}

Contact: {{legalEmail}}
`,
    requiredVariables: [
      'brandEntity',
      'cloudProvider',
      'legalEmail',
      'effectiveDate',
    ],
    generationPrompt:
      'Generate HIPAA BAA between {{brandEntity}} and {{cloudProvider}} for cloud infrastructure hosting PHI. Note: Google Cloud/Firebase provides a standard BAA — this template serves as reference. Key concerns: encryption at rest (AES-256), encryption in transit (TLS 1.2+), access controls, audit logging, data residency. Format: reference document noting Google\'s standard BAA covers Firebase services.',
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 4,
    sortOrder: 4,
    generationRound: 3,
    dependencies: ['5.1-baa-llc-pc'],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 5.5 BAA: PC ↔ Pharmacy ──
  {
    id: '5.5-baa-pc-pharmacy',
    categoryId: 'hipaa',
    title: 'BAA: PC ↔ Pharmacy',
    description:
      'Business Associate Agreement between the Professional Corporation (Covered Entity) and pharmacy partner for direct prescription fulfillment relationship.',
    templateContent: `# Business Associate Agreement

**Between:**
- {{pcEntityName}} ("Covered Entity")
- Pharmacy Partner

Effective Date: {{effectiveDate}}

## Permitted Uses and Disclosures
{{AI_GENERATED_CONTENT}}

## Business Associate Obligations
{{AI_GENERATED_CONTENT}}

## Safeguards
{{AI_GENERATED_CONTENT}}

## Breach Notification
{{AI_GENERATED_CONTENT}}

## Term and Termination
{{AI_GENERATED_CONTENT}}

Contact: {{legalEmail}}
`,
    requiredVariables: [
      'pcEntityName',
      'pharmacyPartner',
      'legalEmail',
      'effectiveDate',
    ],
    generationPrompt:
      'Generate HIPAA BAA between {{pcEntityName}} (Covered Entity) and pharmacy partner for direct prescription fulfillment relationship. Covers: prescription transmission, patient medication history, adverse event reporting, refill coordination. Format: complete legal document.',
    attorneyReviewRequired: true,
    launchBlocker: false,
    estimatedPages: 8,
    sortOrder: 5,
    generationRound: 3,
    dependencies: ['5.1-baa-llc-pc'],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 5.6 HIPAA Privacy Policy (Internal) ──
  {
    id: '5.6-hipaa-privacy-policy',
    categoryId: 'hipaa',
    title: 'HIPAA Privacy Policy (Internal)',
    description:
      'Internal HIPAA Privacy Policy governing how workforce members must handle protected health information, patient rights procedures, and complaint handling.',
    templateContent: `# HIPAA Privacy Policy

**{{brandEntity}}** — Internal Policy

Privacy Officer: {{ceoName}} (Interim)

## PHI Handling Requirements
{{AI_GENERATED_CONTENT}}

## Minimum Necessary Standard
{{AI_GENERATED_CONTENT}}

## Patient Rights Procedures
{{AI_GENERATED_CONTENT}}

## Complaint Handling
{{AI_GENERATED_CONTENT}}

## Sanctions for Violations
{{AI_GENERATED_CONTENT}}

Contact: {{complianceEmail}} | {{privacyEmail}}
`,
    requiredVariables: [
      'brandEntity',
      'ceoName',
      'complianceEmail',
      'privacyEmail',
    ],
    generationPrompt:
      'Generate internal HIPAA Privacy Policy for {{brandEntity}} workforce. Cover: how workforce members must handle PHI, minimum necessary standard, patient rights procedures (access, amendment, accounting of disclosures, restriction requests, confidential communications), complaint handling process, sanctions for violations. Designate Privacy Officer: {{ceoName}} (interim). Contact: {{complianceEmail}}. Format: internal policy document with version, effective date, responsible party, review schedule.',
    attorneyReviewRequired: true,
    launchBlocker: false,
    estimatedPages: 10,
    sortOrder: 6,
    generationRound: 4,
    dependencies: ['1.4-hipaa-npp', '5.1-baa-llc-pc'],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 5.7 HIPAA Security Policy ──
  {
    id: '5.7-hipaa-security-policy',
    categoryId: 'hipaa',
    title: 'HIPAA Security Policy',
    description:
      'Internal HIPAA Security Policy covering administrative, physical, and technical safeguards for the cloud-based platform.',
    templateContent: `# HIPAA Security Policy

**{{brandEntity}}** — Internal Policy

Security Officer: {{ceoName}} (Interim)

## Administrative Safeguards
{{AI_GENERATED_CONTENT}}

## Physical Safeguards
{{AI_GENERATED_CONTENT}}

## Technical Safeguards
{{AI_GENERATED_CONTENT}}

## Cloud Platform Security (Firebase/GCP)
{{AI_GENERATED_CONTENT}}

Contact: {{complianceEmail}}
`,
    requiredVariables: [
      'brandEntity',
      'ceoName',
      'complianceEmail',
    ],
    generationPrompt: `Generate a comprehensive HIPAA Security Policy for {{brandEntity}}. Security Officer: {{ceoName}} (Interim).

SECTION 1 — ADMINISTRATIVE SAFEGUARDS (45 CFR §164.308):
- Risk management program with annual risk assessment
- Workforce security: background checks, role-based access, termination procedures
- Security awareness training: annual HIPAA training for all workforce members
- Access management: unique user IDs, emergency access procedures
- Contingency plan: data backup, disaster recovery, emergency mode operations
- Security incident procedures: detection, response, documentation, reporting to {{complianceEmail}}

SECTION 2 — PHYSICAL SAFEGUARDS (45 CFR §164.310):
- Facility access controls (cloud-based platform — data center security managed by Google Cloud)
- Workstation security: screen lock, encryption, approved software only
- Device and media controls: encryption of portable devices, secure disposal procedures

SECTION 3 — TECHNICAL SAFEGUARDS (45 CFR §164.312):
- Access controls: Firebase Authentication, role-based permissions, automatic session timeout
- Audit controls: Cloud Logging for all PHI access, Firestore audit trail, 6-year log retention
- Integrity controls: Firestore security rules, input validation, checksums on data transfers
- Transmission security: TLS 1.2+ for all data in transit, HTTPS enforced

SECTION 4 — CLOUD PLATFORM SECURITY (Firebase/GCP):
- Encryption: AES-256 at rest (Google-managed keys), TLS 1.2+ in transit
- Firebase Auth for identity and access management
- Firestore security rules enforce document-level access control
- Cloud Functions run in isolated containers with least-privilege IAM roles
- Google Cloud BAA covers all HIPAA-eligible services in use

FORMAT RULES:
- Use actual entity names ({{brandEntity}}, {{ceoName}}, {{complianceEmail}}) — NEVER bracket placeholders
- Include version number, effective date, next review date, and responsible party at the top
- Do NOT include meta-commentary or notes about the generation process
- Professional internal policy document format
- End with approval signature block`,
    attorneyReviewRequired: true,
    launchBlocker: false,
    estimatedPages: 15,
    sortOrder: 7,
    generationRound: 4,
    dependencies: ['1.4-hipaa-npp', '5.1-baa-llc-pc'],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 5.8 Breach Notification Policy ──
  {
    id: '5.8-breach-notification-policy',
    categoryId: 'hipaa',
    title: 'Breach Notification Policy',
    description:
      'Internal policy defining breach per HIPAA, discovery/investigation procedures, risk assessment, and notification timelines.',
    templateContent: `# Breach Notification Policy

**{{brandEntity}}** — Internal Policy

## Definition of Breach
{{AI_GENERATED_CONTENT}}

## Discovery and Investigation
{{AI_GENERATED_CONTENT}}

## Risk Assessment (4-Factor Test)
{{AI_GENERATED_CONTENT}}

## Notification Timelines
{{AI_GENERATED_CONTENT}}

## Breach Log Maintenance
{{AI_GENERATED_CONTENT}}

Contact: {{privacyEmail}} | {{legalEmail}} | {{complianceEmail}}
`,
    requiredVariables: [
      'brandEntity',
      'ceoName',
      'complianceEmail',
      'legalEmail',
      'privacyEmail',
    ],
    generationPrompt:
      'Generate HIPAA Breach Notification Policy for {{brandEntity}}. Define breach per HIPAA. Discovery/investigation procedures. Risk assessment (4-factor test: nature/extent of PHI, unauthorized person, PHI actually acquired/viewed, mitigation extent). Notification timelines: individual max 60 days, HHS annually if <500 affected / within 60 days if >=500, media within 60 days if >=500 in a state. Breach log maintenance. Mirrors Data Ops breach detection pipeline. Contact: {{privacyEmail}}, {{legalEmail}}. Format: internal policy.',
    attorneyReviewRequired: true,
    launchBlocker: false,
    estimatedPages: 8,
    sortOrder: 8,
    generationRound: 4,
    dependencies: ['1.4-hipaa-npp', '5.1-baa-llc-pc'],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 5.9 HIPAA Training Acknowledgment Form ──
  {
    id: '5.9-hipaa-training-acknowledgment',
    categoryId: 'hipaa',
    title: 'HIPAA Training Acknowledgment Form',
    description:
      'Attestation form for workforce members confirming completion of HIPAA training. Annual renewal required. Maintained 6 years per HIPAA retention.',
    templateContent: `# HIPAA Training Acknowledgment

**{{brandEntity}}**

I acknowledge that I have completed HIPAA training covering the following topics:

- PHI handling procedures
- Minimum necessary standard
- Patient rights
- Breach reporting
- Sanctions for violations

**Printed Name:** ___________________________

**Signature:** ___________________________

**Date:** ___________________________

Annual renewal required. Records maintained 6 years per HIPAA retention.

Contact: {{complianceEmail}}
`,
    requiredVariables: [
      'brandEntity',
      'complianceEmail',
    ],
    generationPrompt:
      'Generate HIPAA Training Acknowledgment form for {{brandEntity}}. Attestation of completed training. Topics covered: PHI handling, minimum necessary, patient rights, breach reporting, sanctions. Fields: printed name, signature, date. Annual renewal required. Maintained 6 years per HIPAA retention. Format: clean printable form.',
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 1,
    sortOrder: 9,
    generationRound: 5,
    dependencies: [],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 5.10 HIPAA Risk Assessment Template ──
  {
    id: '5.10-risk-assessment-template',
    categoryId: 'hipaa',
    title: 'HIPAA Risk Assessment Template',
    description:
      'Structured template for conducting HIPAA-required risk assessments covering asset inventory, threat identification, vulnerability assessment, and mitigation measures.',
    templateContent: `# HIPAA Risk Assessment

**{{brandEntity}}**

Risk Assessment Officer: {{ceoName}} (Interim)

## Asset Inventory
{{AI_GENERATED_CONTENT}}

## Threat Identification
{{AI_GENERATED_CONTENT}}

## Vulnerability Assessment
{{AI_GENERATED_CONTENT}}

## Risk Rating Matrix
{{AI_GENERATED_CONTENT}}

## Mitigation Measures
{{AI_GENERATED_CONTENT}}

## Residual Risk Acceptance
{{AI_GENERATED_CONTENT}}

Annual reassessment required.
`,
    requiredVariables: [
      'brandEntity',
      'ceoName',
    ],
    generationPrompt:
      'Generate HIPAA Risk Assessment template for {{brandEntity}}. Sections: asset inventory (systems touching PHI — Firebase Firestore, Cloud Functions, claudeProxy, OpenLoop API, pharmacy API), threat identification, vulnerability assessment, risk rating (likelihood x impact matrix), mitigation measures, residual risk acceptance, annual reassessment requirement. Format: structured template with tables.',
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 8,
    sortOrder: 10,
    generationRound: 5,
    dependencies: [],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 5.11 Minimum Necessary Use Policy ──
  {
    id: '5.11-minimum-necessary-policy',
    categoryId: 'hipaa',
    title: 'Minimum Necessary Use Policy',
    description:
      'Policy defining role-based access to PHI with a matrix of which roles can access which data categories.',
    templateContent: `# Minimum Necessary Use Policy

**{{brandEntity}}** — Internal Policy

## Role-Based Access Matrix
{{AI_GENERATED_CONTENT}}

## Access Request Procedures
{{AI_GENERATED_CONTENT}}

## Review and Audit
{{AI_GENERATED_CONTENT}}
`,
    requiredVariables: [
      'brandEntity',
      'ceoName',
      'medicalDirectorName',
    ],
    generationPrompt:
      'Generate Minimum Necessary Use Policy for {{brandEntity}}. Role-based access matrix: CEO ({{ceoName}}): full admin access for operations; Medical Director ({{medicalDirectorName}}): clinical protocols, aggregate data, no individual patient data; Ops Lead: user support, data requests, consent management; Tech Lead: system logs, anonymized data, no direct PHI access; Provider (via OpenLoop): patient\'s own lab data, health profile, prescription authority; Pharmacy: prescription orders, shipping info only. Format: policy with role-access matrix table.',
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 4,
    sortOrder: 11,
    generationRound: 4,
    dependencies: ['1.4-hipaa-npp', '5.1-baa-llc-pc'],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 5.12 HIPAA Sanctions Policy ──
  {
    id: '5.12-sanctions-policy',
    categoryId: 'hipaa',
    title: 'HIPAA Sanctions Policy',
    description:
      'Progressive discipline policy for HIPAA violations including unintentional, negligent, and intentional violation categories.',
    templateContent: `# HIPAA Sanctions Policy

**{{brandEntity}}** — Internal Policy

## Progressive Discipline
{{AI_GENERATED_CONTENT}}

## Documentation Requirements
{{AI_GENERATED_CONTENT}}

## Appeal Process
{{AI_GENERATED_CONTENT}}

Contact: {{complianceEmail}}
`,
    requiredVariables: [
      'brandEntity',
      'ceoName',
      'complianceEmail',
    ],
    generationPrompt: `Generate a HIPAA Sanctions Policy for {{brandEntity}}. Privacy/Security Officer: {{ceoName}} (Interim).

SECTION 1 — PROGRESSIVE DISCIPLINE:

Level 1 — Unintentional Violation (first occurrence, no malice):
- Mandatory retraining within 14 days
- Written warning placed in personnel file
- Supervised access for 30 days
- Reported to {{complianceEmail}}

Level 2 — Pattern of Negligence (repeat violations or failure to complete retraining):
- Suspension of system access pending investigation
- Performance improvement plan (PIP) with 60-day monitoring
- Potential reassignment away from PHI access
- Reported to {{complianceEmail}} and {{ceoName}}

Level 3 — Intentional or Willful Violation:
- Immediate termination of employment or contract
- Referral to legal counsel for potential civil/criminal liability
- Report to HHS Office for Civil Rights if required
- Preservation of all evidence and audit logs

SECTION 2 — DOCUMENTATION REQUIREMENTS:
- All sanctions documented in writing with date, description, evidence, and action taken
- Employee/contractor acknowledgment signature required
- Documentation retained for minimum 6 years per HIPAA retention requirements
- Maintained by {{complianceEmail}}

SECTION 3 — APPEAL PROCESS:
- Written appeal within 14 days of sanction
- Review by {{ceoName}} or designated compliance committee
- Decision within 30 days of appeal
- Decision is final

FORMAT RULES:
- Use actual entity names ({{brandEntity}}, {{ceoName}}, {{complianceEmail}}) — NEVER bracket placeholders
- Include version number, effective date, and responsible party at the top
- Do NOT include meta-commentary or notes about the generation process
- Professional internal policy document format
- End with acknowledgment signature block`,
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 3,
    sortOrder: 12,
    generationRound: 4,
    dependencies: ['1.4-hipaa-npp', '5.1-baa-llc-pc'],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 5.13 Device and Media Controls Policy ──
  {
    id: '5.13-device-media-controls',
    categoryId: 'hipaa',
    title: 'Device and Media Controls Policy',
    description:
      'Policy covering laptop/workstation encryption, mobile device management, removable media restrictions, disposal procedures, and BYOD requirements.',
    templateContent: `# Device and Media Controls Policy

**{{brandEntity}}** — Internal Policy

## Encryption Requirements
{{AI_GENERATED_CONTENT}}

## Mobile Device Management
{{AI_GENERATED_CONTENT}}

## Removable Media Restrictions
{{AI_GENERATED_CONTENT}}

## Disposal Procedures
{{AI_GENERATED_CONTENT}}

## BYOD Policy
{{AI_GENERATED_CONTENT}}

## Cloud Storage Restrictions
{{AI_GENERATED_CONTENT}}

Contact: {{complianceEmail}}
`,
    requiredVariables: [
      'brandEntity',
      'complianceEmail',
    ],
    generationPrompt:
      'Generate Device and Media Controls Policy for {{brandEntity}}. Laptop/workstation encryption requirements, mobile device management, removable media restrictions, disposal procedures (data wiping, certificate of destruction), BYOD policy, cloud storage restrictions (approved services only: Firebase, Google Drive). Format: internal policy.',
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 4,
    sortOrder: 13,
    generationRound: 4,
    dependencies: ['1.4-hipaa-npp', '5.1-baa-llc-pc'],
    anchorSections: [],
    inheritedProvisions: [],
  },
];
